import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isNativeApp } from "@/utils/platform-detection";
import { pickFromCamera, pickFromLibrary } from "@/utils/nativeMediaPicker";
import { COLLECTION_DETAIL } from "@/routes";
import {
  resolveMenuSelection,
  type ContentType,
  type MenuItemKey,
} from "@/components/navigation/addContentFlowModel";
import type { CollectionVisibility } from "@/types/collections";

interface NewCollectionRef {
  id: string;
  title: string;
  visibility: CollectionVisibility;
}

export const useAddContentFlow = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false);
  const [newCollection, setNewCollection] = useState<NewCollectionRef | null>(null);
  const [contentType, setContentType] = useState<ContentType>("link");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const openModal = useCallback((type: ContentType, files: File[] = []) => {
    setContentType(type);
    setPendingFiles(files);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingFiles([]);
  }, []);

  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  const selectMenuItem = useCallback(
    async (key: MenuItemKey) => {
      setIsMenuOpen(false);
      const action = resolveMenuSelection(key, isNativeApp());
      if (action.type === "create-collection") {
        setCreateCollectionOpen(true);
        return;
      }
      if (action.type === "open-modal") {
        openModal(action.contentType);
        return;
      }
      try {
        const files = action.source === "camera" ? await pickFromCamera() : await pickFromLibrary();
        if (files.length > 0) openModal("image", files);
      } catch (err) {
        console.log("[AddContentFlow] Picker cancelled:", err);
      }
    },
    [openModal],
  );

  const handleCollectionCreated = useCallback(
    (collectionId: string, title: string, visibility: CollectionVisibility) => {
      setNewCollection({ id: collectionId, title, visibility });
    },
    [],
  );

  const handleAddItemsClose = useCallback(
    (open: boolean) => {
      if (!open && newCollection) {
        const { id } = newCollection;
        setNewCollection(null);
        navigate(COLLECTION_DETAIL({ collectionId: id }));
      }
    },
    [newCollection, navigate],
  );

  return {
    isMenuOpen,
    toggleMenu,
    closeMenu,
    isModalOpen,
    openModal,
    closeModal,
    createCollectionOpen,
    setCreateCollectionOpen,
    newCollection,
    contentType,
    pendingFiles,
    selectMenuItem,
    handleCollectionCreated,
    handleAddItemsClose,
  };
};

export type AddContentFlow = ReturnType<typeof useAddContentFlow>;
